/**
 * Video analytics tracking helpers for web platform.
 * All video events use the `video_` prefix and snake_case naming.
 */
import { trackEvent, getPlatformType } from './analytics'

// ─── Types ──────────────────────────────────────────────────────────────────

interface VideoEventData {
  [key: string]: string | number | boolean | null | undefined
}

interface GameLike {
  id?: string | null
  sport_id?: string | number
  generic_sport_id?: string | number | null
  feature_flags?: Record<string, boolean>
}

type VideoType = 'livestream' | 'replay' | 'youtube' | 's3_upload' | 'unknown'

// ─── Common Helpers ─────────────────────────────────────────────────────────

export { getPlatformType }

export const getVideoCommonProps = (game: GameLike | null): VideoEventData => ({
  game_id: game?.id || null,
  platform: getPlatformType(),
  sport_id: game?.sport_id || null,
  is_generic_sport: game?.generic_sport_id != null,
  is_livestreaming_enabled: game?.feature_flags?.livestreaming === true,
})

export const getVideoType = (
  videoUrl: string | undefined,
  broadcastState: string | undefined
): VideoType => {
  if (broadcastState === 'live') {
    return 'livestream'
  }
  if (videoUrl?.includes('youtube.com') || videoUrl?.includes('youtu.be')) {
    return 'youtube'
  }
  if (
    videoUrl?.includes('s3.amazonaws.com') ||
    videoUrl?.includes('amazonaws.com')
  ) {
    return 's3_upload'
  }
  if (videoUrl?.includes('stream.mux.com') || videoUrl?.includes('mux.com')) {
    return 'replay'
  }
  return 'unknown'
}

const trackVideoEvent = (eventName: string, data: VideoEventData): void => {
  trackEvent(
    eventName,
    data as Record<string, string | number | boolean | undefined>
  )
}

// ─── 4.1 Player Lifecycle ───────────────────────────────────────────────────

/** VW-01 */
export const trackVideoPlayerLoaded = (
  game: GameLike | null,
  params: {
    videoType: string
    broadcastState: string
    streamType?: string | null
    availableStreamCount: number
  }
): void => {
  trackVideoEvent('video_player_loaded', {
    ...getVideoCommonProps(game),
    video_type: params.videoType,
    broadcast_state: params.broadcastState || 'idle',
    stream_type: params.streamType || null,
    available_stream_count: params.availableStreamCount,
  })
}

/** VW-02 */
export const trackVideoPlayerError = (
  game: GameLike | null,
  params: {
    videoType: string
    errorMessage: string
    errorCode?: string | null
    streamType?: string | null
    videoUrlDomain?: string | null
  }
): void => {
  trackVideoEvent('video_player_error', {
    ...getVideoCommonProps(game),
    video_type: params.videoType,
    error_message: (params.errorMessage || '').substring(0, 100),
    error_code: params.errorCode || null,
    stream_type: params.streamType || null,
    video_url_domain: params.videoUrlDomain || null,
  })
}

/** VW-03 */
export const trackVideoPlayerRetry = (
  game: GameLike | null,
  params: { broadcastState: string }
): void => {
  trackVideoEvent('video_player_retry', {
    ...getVideoCommonProps(game),
    broadcast_state: params.broadcastState || 'error',
  })
}

// ─── 4.2 Playback Actions ───────────────────────────────────────────────────

/** VW-04 */
export const trackVideoPlayStarted = (
  game: GameLike | null,
  params: {
    videoType: string
    streamType?: string | null
    isAutoplay?: boolean
    isLive?: boolean
    gameStarted?: boolean
    gameCompleted?: boolean
  }
): void => {
  trackVideoEvent('video_play_started', {
    ...getVideoCommonProps(game),
    video_type: params.videoType,
    stream_type: params.streamType || null,
    is_autoplay: params.isAutoplay || false,
    is_live: params.isLive || false,
    game_started: params.gameStarted || false,
    game_completed: params.gameCompleted || false,
  })
}

/** VW-05 */
export const trackVideoPlayPaused = (
  game: GameLike | null,
  params: {
    videoType: string
    streamType?: string | null
    watchDurationSeconds: number
    videoPositionSeconds: number
    isLive?: boolean
  }
): void => {
  trackVideoEvent('video_play_paused', {
    ...getVideoCommonProps(game),
    video_type: params.videoType,
    stream_type: params.streamType || null,
    watch_duration_seconds: Math.round(params.watchDurationSeconds || 0),
    video_position_seconds: Math.round(params.videoPositionSeconds || 0),
    is_live: params.isLive || false,
  })
}

/** VW-06 */
export const trackVideoPlayResumed = (
  game: GameLike | null,
  params: { videoType: string; pauseDurationSeconds: number }
): void => {
  trackVideoEvent('video_play_resumed', {
    ...getVideoCommonProps(game),
    video_type: params.videoType,
    pause_duration_seconds: Math.round(params.pauseDurationSeconds || 0),
  })
}

/** VW-07 */
export const trackVideoPlaySeeked = (
  game: GameLike | null,
  params: {
    videoType: string
    seekFromSeconds: number
    seekToSeconds: number
    isLive?: boolean
    videoDurationSeconds: number
  }
): void => {
  trackVideoEvent('video_play_seeked', {
    ...getVideoCommonProps(game),
    video_type: params.videoType,
    seek_from_seconds: Math.round(params.seekFromSeconds || 0),
    seek_to_seconds: Math.round(params.seekToSeconds || 0),
    seek_direction:
      (params.seekToSeconds || 0) > (params.seekFromSeconds || 0)
        ? 'forward'
        : 'backward',
    is_live: params.isLive || false,
    video_duration_seconds: Math.round(params.videoDurationSeconds || 0),
  })
}

/** VW-08 */
export const trackVideoWatchMilestone = (
  game: GameLike | null,
  params: {
    videoType: string
    streamType?: string | null
    milestone: string
    watchDurationSeconds: number
    isLive?: boolean
  }
): void => {
  trackVideoEvent('video_watch_milestone', {
    ...getVideoCommonProps(game),
    video_type: params.videoType,
    stream_type: params.streamType || null,
    milestone: params.milestone,
    watch_duration_seconds: Math.round(params.watchDurationSeconds || 0),
    is_live: params.isLive || false,
  })
}

/** VW-09 */
export const trackVideoWatchEnded = (
  game: GameLike | null,
  params: {
    videoType: string
    streamType?: string | null
    totalWatchDurationSeconds: number
    totalSessionDurationSeconds: number
    videoDurationSeconds?: number | null
    completionPercentage?: number | null
    isLive?: boolean
    endedReason: string
    pauseCount: number
    seekCount: number
    streamsWatchedCount: number
  }
): void => {
  trackVideoEvent('video_watch_ended', {
    ...getVideoCommonProps(game),
    video_type: params.videoType,
    stream_type: params.streamType || null,
    total_watch_duration_seconds: Math.round(
      params.totalWatchDurationSeconds || 0
    ),
    total_session_duration_seconds: Math.round(
      params.totalSessionDurationSeconds || 0
    ),
    video_duration_seconds:
      params.videoDurationSeconds != null
        ? Math.round(params.videoDurationSeconds)
        : null,
    completion_percentage:
      params.completionPercentage != null
        ? Math.round(params.completionPercentage)
        : null,
    is_live: params.isLive || false,
    ended_reason: params.endedReason || 'user_navigated_away',
    pause_count: params.pauseCount || 0,
    seek_count: params.seekCount || 0,
    streams_watched_count: params.streamsWatchedCount || 1,
  })
}

// ─── 4.3 Stream Selection ───────────────────────────────────────────────────

/** VW-10 */
export const trackVideoStreamSelectorOpened = (
  game: GameLike | null,
  params: {
    availableStreamCount: number
    currentStreamType?: string | null
    triggerSource: string
  }
): void => {
  trackVideoEvent('video_stream_selector_opened', {
    ...getVideoCommonProps(game),
    available_stream_count: params.availableStreamCount,
    current_stream_type: params.currentStreamType || null,
    trigger_source: params.triggerSource,
  })
}

/** VW-11 */
export const trackVideoStreamSelected = (
  game: GameLike | null,
  params: {
    selectedStreamType: string
    previousStreamType?: string | null
    isStreamLive?: boolean
    hasRecording?: boolean
    isSwitch?: boolean
  }
): void => {
  trackVideoEvent('video_stream_selected', {
    ...getVideoCommonProps(game),
    selected_stream_type: params.selectedStreamType,
    previous_stream_type: params.previousStreamType || null,
    is_stream_live: params.isStreamLive || false,
    has_recording: params.hasRecording || false,
    is_switch: params.isSwitch || false,
  })
}

/** VW-12 */
export const trackVideoAutoplayTriggered = (
  game: GameLike | null,
  params: { streamType?: string | null; timeOnScreenSeconds: number }
): void => {
  trackVideoEvent('video_autoplay_triggered', {
    ...getVideoCommonProps(game),
    stream_type: params.streamType || null,
    time_on_screen_seconds: Math.round(params.timeOnScreenSeconds || 0),
  })
}

// ─── 4.5 Share & Monetization ───────────────────────────────────────────────

/** VW-15 */
export const trackVideoSharePressed = (
  game: GameLike | null,
  params: {
    videoType: string
    streamType?: string | null
    isLive?: boolean
    shareSource: string
  }
): void => {
  trackVideoEvent('video_share_pressed', {
    ...getVideoCommonProps(game),
    video_type: params.videoType,
    stream_type: params.streamType || null,
    is_live: params.isLive || false,
    share_source: params.shareSource,
  })
}

/** VW-16 */
export const trackVideoFreeAccessModalViewed = (
  game: GameLike | null,
  params: { triggerSource: string }
): void => {
  trackVideoEvent('video_free_access_modal_viewed', {
    ...getVideoCommonProps(game),
    trigger_source: params.triggerSource,
  })
}

/** VW-17 */
export const trackVideoFreeAccessModalAction = (
  game: GameLike | null,
  params: { action: string }
): void => {
  trackVideoEvent('video_free_access_modal_action', {
    ...getVideoCommonProps(game),
    action: params.action,
  })
}

// ─── 4.6 Broadcast State Overlays ───────────────────────────────────────────

/** VW-18 */
export const trackVideoOverlayViewed = (
  game: GameLike | null,
  params: { overlayState: string; hasReplayUrl?: boolean }
): void => {
  trackVideoEvent('video_overlay_viewed', {
    ...getVideoCommonProps(game),
    overlay_state: params.overlayState,
    has_replay_url: params.hasReplayUrl || false,
  })
}

/** VW-19 */
export const trackVideoReplayRequested = (
  game: GameLike | null,
  params: { streamType?: string | null }
): void => {
  trackVideoEvent('video_replay_requested', {
    ...getVideoCommonProps(game),
    stream_type: params.streamType || null,
  })
}

// ─── 4.7 Video Quality & Buffering ──────────────────────────────────────────

/** VW-20 */
export const trackVideoBufferStarted = (
  game: GameLike | null,
  params: {
    videoType: string
    streamType?: string | null
    videoPositionSeconds: number
    isLive?: boolean
  }
): void => {
  trackVideoEvent('video_buffer_started', {
    ...getVideoCommonProps(game),
    video_type: params.videoType,
    stream_type: params.streamType || null,
    video_position_seconds: Math.round(params.videoPositionSeconds || 0),
    is_live: params.isLive || false,
  })
}

/** VW-21 */
export const trackVideoBufferEnded = (
  game: GameLike | null,
  params: {
    bufferDurationMs: number
    videoType: string
    streamType?: string | null
  }
): void => {
  trackVideoEvent('video_buffer_ended', {
    ...getVideoCommonProps(game),
    buffer_duration_ms: Math.round(params.bufferDurationMs || 0),
    video_type: params.videoType,
    stream_type: params.streamType || null,
  })
}

// ─── 6. Video Upload ────────────────────────────────────────────────────────

/** VU-01 */
export const trackVideoUploadModalOpened = (
  game: GameLike | null,
  params: { isEditing?: boolean; hasExistingVideo?: boolean }
): void => {
  trackVideoEvent('video_upload_modal_opened', {
    ...getVideoCommonProps(game),
    is_editing: params.isEditing || false,
    has_existing_video: params.hasExistingVideo || false,
  })
}

/** VU-02 */
export const trackVideoUploadTabSwitched = (
  game: GameLike | null,
  params: { tab: string }
): void => {
  trackVideoEvent('video_upload_tab_switched', {
    ...getVideoCommonProps(game),
    tab: params.tab,
  })
}

/** VU-03 */
export const trackVideoUploadStarted = (
  game: GameLike | null,
  params: { uploadMethod: string }
): void => {
  trackVideoEvent('video_upload_started', {
    ...getVideoCommonProps(game),
    upload_method: params.uploadMethod,
  })
}

/** VU-04 */
export const trackVideoUploadCompleted = (
  game: GameLike | null,
  params: {
    uploadMethod: string
    fileName?: string | null
    isEditing?: boolean
  }
): void => {
  trackVideoEvent('video_upload_completed', {
    ...getVideoCommonProps(game),
    upload_method: params.uploadMethod,
    file_name: params.fileName || null,
    is_editing: params.isEditing || false,
  })
}

/** VU-05 */
export const trackVideoUploadFailed = (
  game: GameLike | null,
  params: { uploadMethod: string; errorMessage: string }
): void => {
  trackVideoEvent('video_upload_failed', {
    ...getVideoCommonProps(game),
    upload_method: params.uploadMethod,
    error_message: (params.errorMessage || '').substring(0, 100),
  })
}

/** VU-06 */
export const trackVideoUploadModalClosed = (
  game: GameLike | null,
  params: {
    wasUploadInProgress?: boolean
    activeTab?: string
  }
): void => {
  trackVideoEvent('video_upload_modal_closed', {
    ...getVideoCommonProps(game),
    was_upload_in_progress: params.wasUploadInProgress || false,
    active_tab: params.activeTab || 'video_upload',
  })
}

// ─── 7. Livestream Management (Web) ─────────────────────────────────────────

/** LM-01 */
export const trackVideoLivestreamsTabViewed = (
  game: GameLike | null,
  params: { livestreamCount: number; hasVideographerPermission?: boolean }
): void => {
  trackVideoEvent('video_livestreams_tab_viewed', {
    ...getVideoCommonProps(game),
    livestream_count: params.livestreamCount,
    has_videographer_permission: params.hasVideographerPermission || false,
  })
}

/** LM-02 */
export const trackVideoLivestreamDetailsViewed = (
  game: GameLike | null,
  params: { livestreamType: string; livestreamStatus: string }
): void => {
  trackVideoEvent('video_livestream_details_viewed', {
    ...getVideoCommonProps(game),
    livestream_type: params.livestreamType,
    livestream_status: params.livestreamStatus,
  })
}

/** LM-03 */
export const trackVideoLivestreamDeleteRequested = (
  game: GameLike | null,
  params: { livestreamType: string; livestreamStatus: string }
): void => {
  trackVideoEvent('video_livestream_delete_requested', {
    ...getVideoCommonProps(game),
    livestream_type: params.livestreamType,
    livestream_status: params.livestreamStatus,
  })
}

/** LM-04 */
export const trackVideoLivestreamDeleted = (
  game: GameLike | null,
  params: { livestreamType: string }
): void => {
  trackVideoEvent('video_livestream_deleted', {
    ...getVideoCommonProps(game),
    livestream_type: params.livestreamType,
  })
}

/** LM-05 */
export const trackVideoVideographerModalOpened = (
  game: GameLike | null
): void => {
  trackVideoEvent('video_videographer_modal_opened', {
    ...getVideoCommonProps(game),
  })
}

/** LM-06 */
export const trackVideoVideographerAssigned = (
  game: GameLike | null,
  params: { teamSide: string }
): void => {
  trackVideoEvent('video_videographer_assigned', {
    ...getVideoCommonProps(game),
    team_side: params.teamSide,
  })
}

/** LM-07 */
export const trackVideoVideographerRemoved = (
  game: GameLike | null,
  params: { teamSide: string }
): void => {
  trackVideoEvent('video_videographer_removed', {
    ...getVideoCommonProps(game),
    team_side: params.teamSide,
  })
}

/** LM-08 */
export const trackVideoAddLivestreamClicked = (
  game: GameLike | null,
  params: { existingLivestreamCount: number }
): void => {
  trackVideoEvent('video_add_livestream_clicked', {
    ...getVideoCommonProps(game),
    existing_livestream_count: params.existingLivestreamCount,
  })
}
